import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import styles from "./Products.module.css";
import {
  ArrowRightIcon,
  BriefcaseIcon,
  LandmarkIcon,
  ShareIcon,
  ShieldCheckIcon,
  TrendingUpIcon,
  UserPlusIcon,
} from "@/components/icons";
import { MainLayout } from "@/layouts/MainLayout";
import { useAuth } from "@/hooks";
import { productsApi } from "@/api/products.api";
import { agentApi } from "@/api/agent.api";
import { CreateUtmLinkModal } from "@/components/CreateUtmLinkModal";
import { getWhitelabelAgentId, checkAndPersistWhitelabelAgentId } from "@/utils/branding";

interface ProductContent {
  metric?: string;
  label?: string;
  tag?: string;
  bestBenefit?: string;
  benefits?: string[];
  colorClass?: string;
  [key: string]: any;
}

interface Product {
  id: string;
  code: string;
  name: string;
  category: string;
  lender: string;
  imageUrl?: string;
  status: string;
  isPanIndia: boolean;
  content?: ProductContent;
  details?: any;
}

interface UIProduct extends Product {
  title: string;
  sub: string;
  icon: React.ReactNode;
  metric: React.ReactNode;
  label: React.ReactNode;
  colorClass: string;
  tag?: string;
  approval: string;
  bestBenefit: string | null;
  externalUrl?: string; // Added for external redirection
  isCard?: boolean;
}

interface ProductGroup {
  personalLoans: UIProduct[];
  businessLoans: UIProduct[];
  savings: UIProduct[];
  cards: UIProduct[];
  lap: UIProduct[];
  demat: UIProduct[];
}

const TickIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    width="20"
    height="20"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const getProductStyle = (category: string, index: number) => {
  const colors = ["orange500", "emerald500", "indigo600", "sky600", "blue600"];
  const icons = [
    <BriefcaseIcon className={styles.iconSvg} key="brief" />,
    <TrendingUpIcon className={styles.iconSvg} key="trend" />,
    <ShieldCheckIcon className={styles.iconSvg} key="shield" />,
    <LandmarkIcon className={styles.iconSvg} key="land" />,
    <UserPlusIcon className={styles.iconSvg} key="user" />,
  ];
  return {
    color: colors[index % colors.length],
    icon: icons[index % icons.length],
  };
};

const getApprovalFromBenefits = (p: Product): string => {
  const benefits = p.content?.benefits || [];
  const approvalStr = benefits.find((b) =>
    b.toLowerCase().includes("approval rate"),
  );

  if (approvalStr) {
    const val = approvalStr.split(":")[1]?.trim();
    if (val) return val;
  }

  return "Good";
};

const getAllStrings = (obj: any): string[] => {
  let strings: string[] = [];
  if (!obj) return strings;

  if (typeof obj === "string") {
    strings.push(obj);
  } else if (Array.isArray(obj)) {
    obj.forEach((item) => {
      strings = strings.concat(getAllStrings(item));
    });
  } else if (typeof obj === "object") {
    Object.values(obj).forEach((value) => {
      strings = strings.concat(getAllStrings(value));
    });
  }
  return strings;
};

const getBestBenefitText = (p: Product): string | null => {
  if (
    p.content?.bestBenefit &&
    typeof p.content.bestBenefit === "string" &&
    p.content.bestBenefit.trim().length > 0
  ) {
    const text = p.content.bestBenefit.trim();
    const lower = text.toLowerCase();
    if (!lower.includes("joining fee") && !lower.includes("annual fee")) {
      return text;
    }
  }

  if (p.content?.benefits && Array.isArray(p.content.benefits)) {
    const candidates = p.content.benefits.filter((b) => {
      if (typeof b !== "string" || b.trim().length === 0) return false;
      const lower = b.toLowerCase();
      if (lower.includes("approval rate")) return false;
      if (lower.includes("joining fee") || lower.includes("annual fee"))
        return false;
      return true;
    });

    if (candidates.length > 0) {
      const bestCandidate = candidates[0];
      const cleanText = bestCandidate.trim();
      const wordCount = cleanText.split(/\s+/).length;
      const charCount = cleanText.length;

      if (wordCount <= 5 && charCount <= 28) {
        return cleanText;
      }
    }
  }
  return null;
};

const parseMoneyValue = (amount: string, unit: string | undefined): number => {
  let val = parseFloat(amount.replace(/,/g, ""));
  if (isNaN(val)) return 0;

  if (unit) {
    const u = unit.toLowerCase();
    if (u.includes("lakh") || u.includes("lac")) val *= 100000;
    else if (u.includes("cr")) val *= 10000000;
    else if (u.includes("k")) val *= 1000;
    else if (u.includes("m")) val *= 1000000;
  }
  return val;
};

const getSmartMetric = (p: Product) => {
  const c = p.content || {};
  const allText = [...getAllStrings(p.content), ...getAllStrings(p.details)];
  const cat = (p.category || "").toLowerCase();

  if (cat.includes("card")) {
    const joiningStr =
      allText.find((s) => s.toLowerCase().includes("joining fees")) ||
      "Joining Fees: ₹0";
    const annualStr =
      allText.find((s) => s.toLowerCase().includes("annual fees")) ||
      "Annual Fees: ₹0";

    const cleanJoin = joiningStr.split(":")[1]?.trim() || joiningStr;
    const cleanAnnual = annualStr.split(":")[1]?.trim() || annualStr;

    return {
      value: (
        <div className={styles.feeMetric}>
          <span className={styles.feeLine}>Joining Fees: {cleanJoin}</span>
          <span className={styles.feeLine}>Annual Fees: {cleanAnnual}</span>
        </div>
      ),
      label: "Fees",
      isCard: true,
    };
  }

  if (c.metric && c.metric !== "N/A" && c.metric.trim() !== "") {
    return { value: c.metric, label: c.label || "Details" };
  }

  const rateRegex = /(\d{1,2}(\.\d{1,2})?)\s?%/;
  const moneyRegexGlobal =
    /(?:₹|Rs\.?)\s?(\d+(?:,\d+)*(?:\.\d+)?)\s?(Lakh|Lac|Cr|Crore|k|m)?/gi;

  let bestRate = null;
  let maxMoneyValue = 0;
  let bestMoney = null;

  for (const text of allText) {
    if (!text || typeof text !== "string" || text.length > 150) continue;
    const lowerText = text.toLowerCase();

    if (lowerText.includes("fee") || lowerText.includes("charge")) continue;

    const rateMatch = text.match(rateRegex);
    if (rateMatch) {
      const label = lowerText.includes("cashback")
        ? "Cashback"
        : "Interest Rate";
      if (!bestRate) bestRate = { value: rateMatch[0], label };
    }

    if (
      lowerText.includes("salary") ||
      lowerText.includes("income") ||
      lowerText.includes("job")
    )
      continue;

    const matches = Array.from(text.matchAll(moneyRegexGlobal));
    for (const m of matches) {
      const valStr = m[1];
      const unitStr = m[2];
      const numericVal = parseMoneyValue(valStr, unitStr);
      const fullMatch = m[0];

      let label = "Max Amount";
      if (lowerText.includes("limit")) label = "Credit Limit";

      if (numericVal > maxMoneyValue) {
        maxMoneyValue = numericVal;
        bestMoney = { value: fullMatch, label };
      }
    }
  }

  if (cat.includes("saving") && bestRate) return bestRate;
  if (cat.includes("loan") && bestMoney) return bestMoney;
  if (bestRate) return bestRate;
  if (bestMoney) return bestMoney;

  return { value: "View Details", label: "Features" };
};

const Card = ({
  data,
  onShare,
  agentIdOverride,
  agentName,
}: {
  data: UIProduct;
  onShare: (data: UIProduct) => void;
  agentIdOverride?: string | null;
  agentName?: string | null;
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const iconBgClass = styles[data.colorClass] || styles.blue600;

  const handleShareClick = () => {
    onShare(data);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  let meterSrc = "/meter.png";
  const approvalLower = data.approval.toLowerCase();

  if (approvalLower.includes("excellent")) {
    meterSrc = "/m_exc.svg";
  } else if (approvalLower.includes("good")) {
    meterSrc = "/m_good.svg";
  }

  const isCreditCard = Boolean(
    data.isCard || (data.category || "").toLowerCase().includes("card")
  );

  return (
    <div className={styles.cardWrapper}>
      {data.bestBenefit && (
        <div className={styles.bestBenefitPill}>{data.bestBenefit}</div>
      )}

      <div className={styles.card}>
        <div className={styles.cardOverlay} />

        <div className={styles.cardHeader}>
          <div className={`${styles.iconBox} ${iconBgClass}`}>
            {data.imageUrl ? (
              <img
                src={data.imageUrl}
                alt={data.title}
                className={styles.cardImage}
              />
            ) : (
              data.icon
            )}
          </div>

          <div className={styles.headerActions}>
            {data.tag && <span className={styles.tag}>{data.tag}</span>}
            <button
              className={styles.shareBtn}
              onClick={handleShareClick}
              title="Share Product"
            >
              <div
                className={`${styles.iconTransition} ${isCopied ? styles.showTick : styles.showShare}`}
              >
                {isCopied ? (
                  <TickIcon className={styles.tickIcon} />
                ) : (
                  <ShareIcon size={20} />
                )}
              </div>
            </button>
          </div>
        </div>

        <div className={styles.content}>
          <h3 className={styles.cardTitle}>{data.title}</h3>
          <p className={styles.cardSub}>{data.sub}</p>
        </div>

        {isCreditCard ? (
          <div className={styles.metricsGrid}>
            <div className={styles.metricLeft}>
              <div className={styles.metricValue}>
                {typeof data.metric === "string" && (
                  <span className={styles.star}>★</span>
                )}
                {data.metric}
              </div>
              {typeof data.metric === "string" && (
                <div className={styles.metricLabel}>{data.label}</div>
              )}
            </div>

            <div className={styles.metricRight}>
              <div className={styles.approvalContainer}>
                <img
                  src={meterSrc}
                  alt={`${data.approval} approval`}
                  className={styles.meterImg}
                />
                <div className={styles.approvalText}>{data.approval}</div>
                <div className={styles.metricLabel}>Approval Odds</div>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.metricsSingle}>
            <div className={styles.approvalContainer}>
              <img
                src={meterSrc}
                alt={`${data.approval} approval`}
                className={styles.meterImg}
              />
              <div className={styles.approvalText}>{data.approval}</div>
              <div className={styles.metricLabel}>Approval Odds</div>
            </div>
          </div>
        )}

        <div className={styles.footerActions}>
          {data.externalUrl ? (
            <a
              href={data.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.viewBtn}
            >
              Enquire Now
              <ArrowRightIcon size={14} className={styles.arrowIcon} />
            </a>
          ) : (
            <Link
              to={`/products/${data.id}${agentIdOverride ? `?agentId=${agentIdOverride}${agentName ? `&agentName=${encodeURIComponent(agentName)}` : ""}` : ""}`}
              className={styles.viewBtn}
            >
              View Details
              <ArrowRightIcon size={14} className={styles.arrowIcon} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export const Products: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const agentIdOverride = searchParams.get("agentId") || null;
  const agentName = searchParams.get("agentName") || null;
  const [zipCode, setZipCode] = useState("");
  const [products, setProducts] = useState<ProductGroup>({
    personalLoans: [],
    businessLoans: [],
    savings: [],
    cards: [],
    lap: [],
    demat: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeFilter, setActiveFilter] = useState<
    "All" | "PersonalLoans" | "BusinessLoans" | "Savings" | "Cards" | "LAP" | "Demat"
  >("All");
  const [toast, setToast] = useState({ show: false, message: "" });
  const [linkMode, setLinkMode] = useState<"landing" | "utm">("landing");
  const [utmModalProduct, setUtmModalProduct] = useState<{
    id: string;
    name: string;
    category: string;
  } | null>(null);
  const [agentId, setAgentId] = useState<string | null>(null);
  const [affiliateCode, setAffiliateCode] = useState<string>("");

  const categorizeProducts = (rawProducts: Product[]): ProductGroup => {
    const group: ProductGroup = {
      personalLoans: [],
      businessLoans: [],
      savings: [],
      cards: [],
      lap: [],
      demat: [],
    };

    if (Array.isArray(rawProducts)) {
      rawProducts.forEach((p, index) => {
        const styles = getProductStyle(p.category || "other", index);
        const smartMetric = getSmartMetric(p);

        const uiProduct: UIProduct = {
          ...p,
          title: p.name,
          sub: p.lender,
          metric: smartMetric.value,
          label: smartMetric.label,
          tag: p.content?.tag,
          colorClass: p.content?.colorClass || styles.color,
          icon: styles.icon,
          approval: getApprovalFromBenefits(p),
          bestBenefit: getBestBenefitText(p),
          isCard:
            smartMetric.isCard || (p.category || "").toLowerCase().includes("card"),
        };

        const cat = (p.category || "").toLowerCase();
        const name = (p.name || "").toLowerCase();
        const isBusiness =
          cat.includes("business") || name.includes("business");

        if (isBusiness && (cat.includes("loan") || name.includes("loan"))) {
          group.businessLoans.push(uiProduct);
        } else if (cat.includes("lap") || name.includes("property")) {
          group.lap.push(uiProduct);
        } else if (cat.includes("demat")) {
          group.demat.push(uiProduct);
        } else if (cat.includes("loan") || name.includes("loan")) {
          group.personalLoans.push(uiProduct);
        } else if (cat.includes("saving") || name.includes("saving")) {
          group.savings.push(uiProduct);
        } else {
          group.cards.push(uiProduct);
        }
      });
    }

    const getScore = (p: UIProduct) => {
      const val = (p.approval || "").toLowerCase();
      if (val.includes("excellent")) return 2;
      if (val.includes("good")) return 1;
      return 0;
    };

    const sorter = (a: UIProduct, b: UIProduct) => getScore(b) - getScore(a);

    group.personalLoans.sort(sorter);
    group.businessLoans.sort(sorter);
    group.savings.sort(sorter);
    group.cards.sort(sorter);

    return group;
  };

  useEffect(() => {
    if (!isAuthenticated) {
      const whitelabelAgentId = getWhitelabelAgentId();
      navigate(whitelabelAgentId ? `/agent-login?agentId=${whitelabelAgentId}` : "/agent-login");
      return;
    }
    fetchProducts();
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchAgentDetails = async () => {
      if (agentIdOverride) {
        setAgentId(agentIdOverride);
        return;
      }

      try {
        const storedAgentId =
          localStorage.getItem("agent_id") || localStorage.getItem("agentId");

        if (!storedAgentId) return;

        const response = await agentApi.getAgentDetails({
          query_type: "get_agent_details",
          agent_id: storedAgentId,
        });

        const isSuccess =
          (response.status === "Success" || response.message === "Success") &&
          response.response &&
          Array.isArray(response.response) &&
          response.response.length > 0;

        if (isSuccess) {
          const data = response.response[0];
          setAgentId(String(data.agent_id));
          if (data.affiliate_code) setAffiliateCode(String(data.affiliate_code));
          if (data.parent_agent_id) checkAndPersistWhitelabelAgentId(data.parent_agent_id);
          if (data.agent_id) checkAndPersistWhitelabelAgentId(data.agent_id);
        }
      } catch (err) {
        console.error("Failed to fetch agent details", err);
      }
    };

    if (isAuthenticated) {
      fetchAgentDetails();
    }
  }, [isAuthenticated, agentIdOverride]);

  const fetchProducts = async () => {
    setLoading(true);
    setError("");
    const result = await productsApi.getProducts({ channel: "AGENT" });
    if (result.status === "Success" && Array.isArray(result.response)) {
      const grouped = categorizeProducts(result.response);
      setProducts(grouped);
    } else {
      setError(result.message || "Failed to fetch products");
    }
    setLoading(false);
  };

  const handleCheck = async () => {
    if (!zipCode || zipCode.length !== 6) return;
    setLoading(true);
    setError("");
    const result = await productsApi.checkEligibility({ pincode: zipCode });
    if (result.status === "Success" && result.response) {
      const eligible = result.response.eligible_products || [];
      const grouped = categorizeProducts(eligible);
      setProducts(grouped);
      if (
        eligible.length === 0 &&
        grouped.personalLoans.length === 0 &&
        grouped.businessLoans.length === 0 &&
        grouped.savings.length === 0 &&
        grouped.cards.length === 0 &&
        grouped.lap.length === 0
      ) {
        setError(`No eligible products found for pincode ${zipCode}`);
      }
    } else {
      setError(result.message || "Eligibility check failed");
    }
    setLoading(false);
  };

  const handleShare = (data: UIProduct) => {
    if (!agentId) {
      setToast({ show: true, message: "Loading agent details..." });
      setTimeout(() => setToast({ show: false, message: "" }), 3000);
      return;
    }

    // UTM mode: open the Create UTM Link modal defaulted to this product.
    if (linkMode === "utm") {
      setUtmModalProduct({ id: data.id, name: data.name, category: data.category });
      return;
    }

    const compositeKey = `${agentId}:${data.id}`;
    const encodedRef = btoa(compositeKey);

    const { getExternalURL } = require("@/config");
    const baseUrl = getExternalURL("CUSTOMER_PORTAL");

    const targetUrl = `${baseUrl}/login?ref=${encodedRef}&next=/product/${data.id}/apply`;

    navigator.clipboard.writeText(targetUrl).then(() => {
      setToast({ show: true, message: "Link copied to clipboard!" });
      setTimeout(() => setToast({ show: false, message: "" }), 3000);
    });
  };

  if (!isAuthenticated) return null;

  return (
    <MainLayout>
      <div className={styles.pageContainer}>
        {/* Header Section */}
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.titleGroup}>
              <h1 className={styles.pageTitle}>
                Products
                {agentName && (
                  <span
                    style={{
                      fontSize: "0.6em",
                      fontWeight: 400,
                      color: "#6b7280",
                      marginLeft: "8px",
                    }}
                  >
                    ({agentName})
                  </span>
                )}
              </h1>
              {agentIdOverride && (
                <p
                  style={{
                    fontSize: "0.85rem",
                    color: "#6b7280",
                    marginTop: "4px",
                  }}
                >
                  Viewing products for: {agentName} (ID: {agentIdOverride})
                </p>
              )}
            </div>
            <p className={styles.pageSubtitle}>
              Explore our range of innovative products
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "8px 0 16px" }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: "#6b7280" }}>
                Share as:
              </span>
              <div
                style={{
                  display: "inline-flex",
                  background: "#f3f4f6",
                  border: "1px solid #e5e7eb",
                  borderRadius: 999,
                  padding: 3,
                }}
              >
                {(["landing", "utm"] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setLinkMode(mode)}
                    style={{
                      padding: "6px 16px",
                      border: "none",
                      borderRadius: 999,
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                      transition: "all 0.15s",
                      background: linkMode === mode ? "#1f2d3d" : "transparent",
                      color: linkMode === mode ? "#fff" : "#6b7280",
                    }}
                  >
                    {mode === "landing" ? "Landing Page" : "UTM Link"}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  const id = agentIdOverride || localStorage.getItem("agent_id") || localStorage.getItem("agentId") || "";
                  navigator.clipboard.writeText(`https://login.clickpe.ai/?agentId=${id}`);
                  setToast({ show: true, message: "Affiliate link copied!" });
                  setTimeout(() => setToast({ show: false, message: "" }), 2500);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "0 16px",
                  height: 40,
                  background: "linear-gradient(90deg, #3b82f6 0%, #4f6df5 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 14,
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: "pointer",
                  boxShadow: "0 4px 16px rgba(59, 130, 246, 0.25)",
                }}
              >
                <ShareIcon size={16} />
                Copy Affiliate Link
              </button>
            </div>
            <div className={styles.filterTabsContainer}>
              {/* Filters */}
              <button
                className={`${styles.filterTab} ${activeFilter === "All" ? styles.filterTabActive : ""}`}
                onClick={() => setActiveFilter("All")}
              >
                All
              </button>
              <button
                className={`${styles.filterTab} ${activeFilter === "PersonalLoans" ? styles.filterTabActive : ""}`}
                onClick={() => setActiveFilter("PersonalLoans")}
              >
                Personal Loans
              </button>
              <button
                className={`${styles.filterTab} ${activeFilter === "BusinessLoans" ? styles.filterTabActive : ""}`}
                onClick={() => setActiveFilter("BusinessLoans")}
              >
                Business Loans
              </button>

              <button
                className={`${styles.filterTab} ${activeFilter === "Savings" ? styles.filterTabActive : ""}`}
                onClick={() => setActiveFilter("Savings")}
              >
                Savings Account
              </button>
              <button
                className={`${styles.filterTab} ${activeFilter === "Cards" ? styles.filterTabActive : ""}`}
                onClick={() => setActiveFilter("Cards")}
              >
                Credit Cards
              </button>
              <button
                className={`${styles.filterTab} ${activeFilter === "LAP" ? styles.filterTabActive : ""}`}
                onClick={() => setActiveFilter("LAP")}
              >
                Loan Against Property
              </button>
              <button
                className={`${styles.filterTab} ${activeFilter === "Demat" ? styles.filterTabActive : ""}`}
                onClick={() => setActiveFilter("Demat")}
              >
                Demat Accounts
              </button>
            </div>
          </div>
          <div className={styles.filterContainer}>
            <label className={styles.filterLabel}>
              Filter products based on customer ZIP code
            </label>
            <div className={styles.filterInputGroup}>
              <input
                type="text"
                className={styles.zipInput}
                placeholder="Enter 6-digit Zip"
                value={zipCode}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val) && val.length <= 6) setZipCode(val);
                }}
              />
              <button
                className={styles.checkBtn}
                onClick={handleCheck}
                disabled={loading}
              >
                {loading ? "..." : "Check"}
              </button>
            </div>
          </div>
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}

        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingPulse}>Loading products...</div>
          </div>
        ) : (
          <div className={styles.whiteContainer}>
            {(activeFilter === "All" || activeFilter === "PersonalLoans") &&
              products.personalLoans.length > 0 && (
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>Personal Loans</h2>
                    <div
                      className={`${styles.divider} ${styles.dividerBlue}`}
                    ></div>
                  </div>
                  <div className={styles.grid}>
                    {products.personalLoans.map((item) => (
                      <Card
                        key={item.id}
                        data={item}
                        onShare={handleShare}
                        agentIdOverride={agentIdOverride}
                        agentName={agentName}
                      />
                    ))}
                  </div>
                </section>
              )}
            {(activeFilter === "All" || activeFilter === "BusinessLoans") &&
              products.businessLoans.length > 0 && (
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>Business Loans</h2>
                    <div
                      className={`${styles.divider} ${styles.dividerBlue}`}
                    ></div>
                  </div>
                  <div className={styles.grid}>
                    {products.businessLoans.map((item) => (
                      <Card
                        key={item.id}
                        data={item}
                        onShare={handleShare}
                        agentIdOverride={agentIdOverride}
                        agentName={agentName}
                      />
                    ))}
                  </div>
                </section>
              )}

            {/* Added LAP Section */}
            {(activeFilter === "All" || activeFilter === "LAP") &&
              products.lap.length > 0 && (
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                      Loan Against Property
                    </h2>
                    <div
                      className={`${styles.divider} ${styles.dividerBlue}`}
                    ></div>
                  </div>
                  <div className={styles.grid}>
                    {products.lap.map((item) => (
                      <Card
                        key={item.id}
                        data={item}
                        onShare={handleShare}
                        agentIdOverride={agentIdOverride}
                        agentName={agentName}
                      />
                    ))}
                  </div>
                </section>
              )}

            {(activeFilter === "All" || activeFilter === "Savings") &&
              products.savings.length > 0 && (
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>Saving Accounts</h2>
                    <div
                      className={`${styles.divider} ${styles.dividerBlue}`}
                    ></div>
                  </div>
                  <div className={styles.grid}>
                    {products.savings.map((item) => (
                      <Card
                        key={item.id}
                        data={item}
                        onShare={handleShare}
                        agentIdOverride={agentIdOverride}
                        agentName={agentName}
                      />
                    ))}
                  </div>
                </section>
              )}
            {(activeFilter === "All" || activeFilter === "Cards") &&
              products.cards.length > 0 && (
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>Credit Cards</h2>
                    <div
                      className={`${styles.divider} ${styles.dividerBlue}`}
                    ></div>
                  </div>
                  <div className={styles.grid}>
                    {products.cards.map((item) => (
                      <Card
                        key={item.id}
                        data={item}
                        onShare={handleShare}
                        agentIdOverride={agentIdOverride}
                        agentName={agentName}
                      />
                    ))}
                  </div>
                </section>
              )}
            {(activeFilter === "All" || activeFilter === "Demat") &&
              products.demat.length > 0 && (
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>Demat Accounts</h2>
                    <div
                      className={`${styles.divider} ${styles.dividerBlue}`}
                    ></div>
                  </div>
                  <div className={styles.grid}>
                    {products.demat.map((item) => (
                      <Card
                        key={item.id}
                        data={item}
                        onShare={handleShare}
                        agentIdOverride={agentIdOverride}
                        agentName={agentName}
                      />
                    ))}
                  </div>
                </section>
              )}
            {products.personalLoans.length === 0 &&
              products.businessLoans.length === 0 &&
              products.lap.length === 0 &&
              products.savings.length === 0 &&
              products.cards.length === 0 &&
              products.demat.length === 0 && (
                <div className={styles.emptyState}>
                  <div className={styles.emptyTitle}>No products found</div>
                  <div className={styles.emptyDesc}>
                    Try checking a different ZIP code or clear the filter.
                  </div>
                  {zipCode && (
                    <button
                      onClick={() => {
                        setZipCode("");
                        fetchProducts();
                      }}
                      className={styles.clearFilterBtn}
                    >
                      Clear Filter
                    </button>
                  )}
                </div>
              )}
          </div>
        )}
        <div
          className={`${styles.toast} ${toast.show ? styles.toastVisible : ""}`}
        >
          <TickIcon className={styles.toastIcon} />
          {toast.message}
        </div>
      </div>
      {utmModalProduct && (
        <CreateUtmLinkModal
          affiliateCode={affiliateCode}
          product={utmModalProduct}
          onClose={() => setUtmModalProduct(null)}
        />
      )}
    </MainLayout>
  );
};
